import React, { useState } from 'react';
import { ClipboardList, AlertTriangle, CheckCircle, FileText, Sparkles, Copy, Download, Save, Trash2, Eye, EyeOff, Send } from 'lucide-react';
import { getQuestionBank, detectRedFlags, generateProjectBrief, type Question, type QuestionSection } from './lib/engine';
import { aiAnalyzeResponses } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

interface SavedForm { id: string; name: string; serviceType: string; answers: Record<string, string>; redFlags: any[]; date: string; }

export default function App() {
  const [serviceType, setServiceType] = useState('web_design');
  const [includeBudget, setIncludeBudget] = useState(true);
  const [includeTimeline, setIncludeTimeline] = useState(true);
  const [includeStakeholders, setIncludeStakeholders] = useState(true);
  const [sections, setSections] = useState<QuestionSection[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [redFlags, setRedFlags] = useState<{ severity: string; message: string }[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [saved, setSaved] = useState<SavedForm[]>(() => JSON.parse(localStorage.getItem('ca_onboarding') || '[]'));
  const [previewMode, setPreviewMode] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const generateForm = () => {
    const bank = getQuestionBank(serviceType);
    const filtered = bank.map(s => ({
      ...s,
      questions: s.questions.filter(q => {
        if (!includeBudget && q.question.toLowerCase().includes('budget')) return false;
        if (!includeTimeline && q.question.toLowerCase().includes('timeline')) return false;
        if (!includeStakeholders && q.question.toLowerCase().includes('stakeholder')) return false;
        return true;
      })
    })).filter(s => s.questions.length > 0);
    setSections(filtered);
    setAnswers({});
    setRedFlags([]);
    setAiAnalysis('');
    setActiveSection(0);
  };

  const updateAnswer = (id: string, value: string) => {
    const next = { ...answers, [id]: value };
    setAnswers(next);
    setRedFlags(detectRedFlags(next));
  };

  const handleAiAnalyze = async () => {
    if (Object.keys(answers).length === 0) return;
    setIsAiLoading(true); setAiAnalysis('');
    await aiAnalyzeResponses(serviceType, answers, c => setAiAnalysis(p => p + c));
    setIsAiLoading(false);
  };

  const saveForm = () => {
    const form: SavedForm = { id: Date.now().toString(), name: `${serviceType.replace('_', ' ')} — ${new Date().toLocaleDateString()}`, serviceType, answers, redFlags, date: new Date().toLocaleDateString() };
    const n = [form, ...saved]; setSaved(n); localStorage.setItem('ca_onboarding', JSON.stringify(n));
  };

  const loadForm = (f: SavedForm) => {
    setServiceType(f.serviceType); setAnswers(f.answers); setRedFlags(f.redFlags);
    const bank = getQuestionBank(f.serviceType);
    setSections(bank);
  };

  const exportBrief = () => {
    const brief = generateProjectBrief(serviceType, answers);
    const blob = new Blob([brief], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `project-brief-${Date.now()}.md`; a.click();
  };

  const exportTypeform = () => {
    const json = { title: `Onboarding — ${serviceType}`, fields: sections.flatMap(s => s.questions.map(q => ({ title: q.question, type: q.type, required: q.required, properties: { description: q.why } }))) };
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `typeform-import-${Date.now()}.json`; a.click();
  };

  const completion = sections.length > 0 ? Math.round((Object.keys(answers).length / sections.flatMap(s => s.questions).length) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">Client Onboarding Form Builder</span></h1>
              <p className="text-xs text-[#8a9bb8]">Question Bank Engine + Red Flag Detector + AI Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sections.length > 0 && (
              <>
                <button onClick={() => setPreviewMode(!previewMode)} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5">
                  {previewMode ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>} {previewMode ? 'Edit' : 'Preview'}
                </button>
                <button onClick={exportBrief} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5"><FileText className="w-3.5 h-3.5"/> Brief</button>
                <button onClick={exportTypeform} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5"/> Typeform</button>
                <button onClick={saveForm} className="px-3 py-1.5 text-xs rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] transition-colors flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save</button>
              </>
            )}
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 space-y-3">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-2 flex items-center gap-2"><ClipboardList className="w-4 h-4"/> Form Config</h2>
            <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Service Type</label>
              <select value={serviceType} onChange={e => setServiceType(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                <option value="web_design">Web Design</option><option value="consulting">Consulting</option><option value="marketing">Marketing</option>
              </select>
            </div>
            <div className="flex items-center justify-between"><label className="text-xs text-[#f0f0f0]">Budget questions</label><input type="checkbox" checked={includeBudget} onChange={e => setIncludeBudget(e.target.checked)} className="w-4 h-4 accent-[#d4af37]"/></div>
            <div className="flex items-center justify-between"><label className="text-xs text-[#f0f0f0]">Timeline questions</label><input type="checkbox" checked={includeTimeline} onChange={e => setIncludeTimeline(e.target.checked)} className="w-4 h-4 accent-[#d4af37]"/></div>
            <div className="flex items-center justify-between"><label className="text-xs text-[#f0f0f0]">Stakeholder questions</label><input type="checkbox" checked={includeStakeholders} onChange={e => setIncludeStakeholders(e.target.checked)} className="w-4 h-4 accent-[#d4af37]"/></div>
            <button onClick={generateForm} className="w-full px-4 py-2.5 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center justify-center gap-1.5">
              <Send className="w-4 h-4"/> Generate Form
            </button>
          </div>

          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Save className="w-4 h-4"/> Saved Forms ({saved.length})</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {saved.map(f => (
                <button key={f.id} onClick={() => loadForm(f)} className="w-full text-left bg-[#132238] rounded-lg p-2.5 border border-white/5 hover:border-[#d4af37]/30 transition-colors">
                  <div className="text-xs font-medium text-[#f0f0f0]">{f.name}</div>
                  <div className="text-[10px] text-[#8a9bb8] mt-0.5">{f.date} · {Object.keys(f.answers).length} answers</div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9 space-y-4">
          {sections.length > 0 && !previewMode && (
            <>
              <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-[#f0f0f0]">Form Progress</h2>
                  <span className="text-sm font-bold text-[#d4af37]">{completion}%</span>
                </div>
                <div className="h-2 bg-[#132238] rounded-full overflow-hidden">
                  <div className="h-full bg-[#d4af37] rounded-full transition-all duration-500" style={{ width: `${completion}%` }}/>
                </div>
              </div>

              {/* Red Flags */}
              {redFlags.length > 0 && (
                <div className="bg-[#0f1d32] rounded-xl border border-red-500/20 p-4">
                  <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> Red Flags Detected ({redFlags.length})</h3>
                  <div className="space-y-2">
                    {redFlags.map((f, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${f.severity === 'high' ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${f.severity === 'high' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black'}`}>{f.severity}</span>
                        </div>
                        <p className="text-xs text-[#f0f0f0]">{f.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {sections.map((s, i) => (
                  <button key={i} onClick={() => setActiveSection(i)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeSection === i ? 'bg-[#d4af37] text-[#0a1628]' : 'bg-[#132238] text-[#8a9bb8] hover:bg-[#1a2d4a]'}`}>
                    {s.name}
                  </button>
                ))}
              </div>

              {/* Active Section */}
              {sections[activeSection] && (
                <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-[#d4af37]">{sections[activeSection].name}</h3>
                  {sections[activeSection].questions.map(q => (
                    <div key={q.id}>
                      <label className="text-sm text-[#f0f0f0] mb-1 flex items-center gap-1.5">
                        {q.required && <span className="text-red-400">*</span>}
                        {q.question}
                        <span className="group relative">
                          <span className="text-[10px] text-[#8a9bb8] cursor-help border border-white/10 rounded px-1">?</span>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-48 p-2 bg-[#132238] border border-white/10 rounded-lg text-[10px] text-[#f0f0f0] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">{q.why}</span>
                        </span>
                      </label>
                      {q.type === 'select' && (
                        <select value={answers[q.id] || ''} onChange={e => updateAnswer(q.id, e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f0f0f0] focus:outline-none focus:border-[#d4af37]/50">
                          <option value="">Select...</option>
                          {q.options?.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {q.type === 'multi_select' && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {q.options?.map(o => (
                            <button key={o} onClick={() => {
                              const current = (answers[q.id] || '').split(',').filter(Boolean);
                              const next = current.includes(o) ? current.filter(x => x !== o) : [...current, o];
                              updateAnswer(q.id, next.join(','));
                            }} className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${(answers[q.id] || '').includes(o) ? 'bg-[#d4af37] text-[#0a1628] font-medium' : 'bg-[#132238] text-[#8a9bb8] border border-white/10 hover:bg-[#1a2d4a]'}`}>
                              {o}
                            </button>
                          ))}
                        </div>
                      )}
                      {q.type === 'textarea' && (
                        <textarea value={answers[q.id] || ''} onChange={e => updateAnswer(q.id, e.target.value)} rows={3} className="w-full bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none"/>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* AI Analysis */}
              <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2"><Sparkles className="w-4 h-4"/> AI Analysis</h3>
                  <button onClick={handleAiAnalyze} disabled={isAiLoading} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-[#d4af37]/30 text-[#d4af37] transition-colors disabled:opacity-50">
                    {isAiLoading ? 'Analyzing...' : 'Analyze Responses'}
                  </button>
                </div>
                <div className="bg-[#070f1a] rounded-lg p-4 text-sm text-[#f0f0f0] whitespace-pre-wrap border border-white/10 min-h-[100px]">{aiAnalysis || 'Click Analyze Responses for AI-powered insights: missing questions, scope recommendations, risk areas, pricing strategy.'}</div>
              </div>
            </>
          )}

          {previewMode && sections.length > 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
              <h2 className="text-sm font-semibold text-[#f0f0f0] mb-4">Client Preview</h2>
              <div className="space-y-6">
                {sections.map(s => (
                  <div key={s.name}>
                    <h3 className="text-xs font-semibold text-[#d4af37] uppercase tracking-wider mb-3">{s.name}</h3>
                    <div className="space-y-3">
                      {s.questions.map(q => (
                        <div key={q.id} className="bg-[#132238] rounded-lg p-3 border border-white/5">
                          <div className="text-sm text-[#f0f0f0] mb-1">{q.question} {q.required && <span className="text-red-400">*</span>}</div>
                          <div className="text-xs text-[#8a9bb8]">{q.why}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sections.length === 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-12 text-center">
              <ClipboardList className="w-12 h-12 text-[#8a9bb8] mx-auto mb-4"/>
              <h2 className="text-lg font-semibold text-[#f0f0f0] mb-2">Generate an Onboarding Form</h2>
              <p className="text-sm text-[#8a9bb8]">Select a service type and configure options, then click Generate Form.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
